import React, { Fragment, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { CardBody, Row, Table } from "reactstrap";
import { getPaginationRange } from "services/utils";
import {
  ColumnFiltersState,
  FilterFn,
  useReactTable,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  flexRender,
} from "@tanstack/react-table";
import { rankItem } from "@tanstack/match-sorter-utils";

export interface CustomerContainerProps {
  columns: any[];
  data: any[];
  customPageSize?: number;
  tableClass?: string;
  theadClass?: string;
  trClass?: string;
  divClass?: string;
  pagination?: {
    page: number;
    total_items: number;
    total_pages: number;
    per_page: number;
  };
  onPageChange?: (page: number) => void;
  limit: number;
  setLimit?: React.Dispatch<React.SetStateAction<number>>;
  setPage: (page: number) => void;
  setRowId?: React.Dispatch<React.SetStateAction<string | null>>;
  onRowClick?: (row: any) => void;
}

const CustomerContainer = ({
  columns,
  data,
  customPageSize,
  tableClass,
  theadClass,
  trClass,
  divClass,
  pagination,
  onPageChange,
  limit,
  setLimit,
  setPage,
  onRowClick,
}: CustomerContainerProps) => {
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [globalFilter, setGlobalFilter] = useState("");

  const fuzzyFilter: FilterFn<any> = (row, columnId, value, addMeta) => {
    const itemRank = rankItem(row.getValue(columnId), value);
    addMeta({ itemRank });
    return itemRank.passed;
  };

  const table = useReactTable({
    columns,
    data,
    filterFns: { fuzzy: fuzzyFilter },
    state: { columnFilters, globalFilter },
    onColumnFiltersChange: setColumnFilters,
    onGlobalFilterChange: setGlobalFilter,
    globalFilterFn: fuzzyFilter,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
    manualPagination: !!pagination,
    pageCount: pagination ? Math.max(1, pagination.total_pages) : undefined,
  });

  const { getHeaderGroups, getRowModel, setPageSize } = table;

  useEffect(() => {
    if (customPageSize) {
      setPageSize(customPageSize);
    }
  }, [customPageSize, setPageSize]);

  return (
    <Fragment>
      <CardBody className={divClass}>
        <div className="table-responsive table-card" style={{ marginBottom: "1rem" }}>
          <Table hover className={tableClass} style={{ marginBottom: 0 }}>
            <thead
              className={theadClass ?? "bg-primary text-white"}
              style={{ fontSize: "0.875rem" }}
            >
              {getHeaderGroups().map((headerGroup) => (
                <tr className={trClass ?? ""} key={headerGroup.id}>
                  {headerGroup.headers.map((header) => (
                    <th
                      key={header.id}
                      onClick={header.column.getToggleSortingHandler?.()}
                      style={{
                        cursor: header.column.getCanSort() ? "pointer" : "default",
                        padding: "12px 16px",
                        fontWeight: 600,
                        border: "none",
                      }}
                    >
                      {header.isPlaceholder
                        ? null
                        : flexRender(header.column.columnDef.header, header.getContext())}
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody>
              {getRowModel().rows.map((row, rowIndex) => (
                <tr
                  key={row.id}
                  onClick={() => onRowClick?.(row.original)}
                  style={{
                    ...(rowIndex % 2 === 1 && { backgroundColor: "rgba(115, 195, 169, 0.12)" }),
                    cursor: onRowClick ? "pointer" : "default",
                  }}
                >
                  {row.getVisibleCells().map((cell) => (
                    <td
                      key={cell.id}
                      style={{
                        verticalAlign: "middle",
                        padding: "12px 16px",
                      }}
                    >
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
      </CardBody>

      {pagination && (
        <Row className="align-items-center mt-2 g-3 border-top pt-4 pb-2">
          <div className="col-sm text-muted">
            Showing{" "}
            <span className="fw-semibold ms-1">
              {Math.min(pagination.per_page * pagination.page, pagination.total_items)}
            </span>{" "}
            of <span className="fw-semibold">{pagination.total_items}</span> Results
          </div>

          <div className="col-sm-auto">
            <ul className="pagination pagination-separated pagination-md mb-0">
              <li className={`page-item ${pagination.page <= 1 ? "disabled" : ""}`}>
                <Link
                  to="#"
                  className="page-link"
                  onClick={(e) => {
                    e.preventDefault();
                    if (pagination.page > 1) onPageChange?.(pagination.page - 1);
                  }}
                >
                  Previous
                </Link>
              </li>

              {getPaginationRange(pagination.page, pagination.total_pages).map((p, idx) => (
                <li key={idx} className={`page-item ${p === pagination.page ? "active" : ""}`}>
                  <Link
                    to="#"
                    className="page-link"
                    onClick={(e) => {
                      e.preventDefault();
                      if (typeof p === "number") onPageChange?.(p);
                    }}
                  >
                    {p}
                  </Link>
                </li>
              ))}

              <li className={`page-item ${pagination.page >= Math.max(1, pagination.total_pages) ? "disabled" : ""}`}>
                <Link
                  to="#"
                  className="page-link"
                  onClick={(e) => {
                    e.preventDefault();
                    if (pagination.page < Math.max(1, pagination.total_pages)) onPageChange?.(pagination.page + 1);
                  }}
                >
                  Next
                </Link>
              </li>
            </ul>
          </div>
        </Row>
      )}
    </Fragment>
  );
};

export default CustomerContainer;
